import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Fade,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import InfoIcon from "@mui/icons-material/Info";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentsIcon from "@mui/icons-material/Payments";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ResponsiveAppBar from "./AppBar";
import { API_BASE_URL } from "../api/config";

const COLORS = ["#2ecc71", "#e67e22", "#3498db"];
const STATUS_COLORS = {
  pending: "#e67e22",
  approved: "#2ecc71",
  rejected: "#e74c3c",
};

const UserDetails = () => {
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sessionUser = JSON.parse(sessionStorage.getItem("user"));
  const userId = sessionUser?.userId;
  const token = localStorage.getItem("userToken");

  useEffect(() => {
    if (!userId || !token) {
      console.log("Missing userId or token:", { userId, token });
      setError("Please log in to view your details");
      setLoading(false);
      navigate("/", { replace: true });
      return;
    }

    const fetchUserDetails = async () => {
      setLoading(true);
      try {
        console.log("Fetching user details with token:", token);
        const res = await fetch(`${API_BASE_URL}/user/get_users_by_id/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
        }
        const data = await res.json();
        console.log("API Response:", data);
        setUserDetails(data);
      } catch (err) {
        console.error("Error fetching user details:", err);
        setError(err.message || "Failed to load user details");
        setUserDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId, token, navigate]);

  const logoutUser = () => {
    localStorage.removeItem("userToken");
    sessionStorage.removeItem("user");
    alert("✅ You've been logged out.");
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          background: "linear-gradient(135deg, #eceff1 0%, #cfd8dc 100%)",
        }}
      >
        <CircularProgress size={50} thickness={5} sx={{ color: "#3498db", mb: 2 }} />
        <Typography variant="h6" color="textSecondary">Loading Dashboard...</Typography>
      </Box>
    );
  }

  if (error || !userDetails) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Typography align="center" variant="h5" color="error">
          {error || "Unable to load user details"}
        </Typography>
      </Box>
    );
  }

  const {
    name = "Unknown",
    email = "Not provided",
    loanType = "N/A",
    loanAmount = 0,
    loanStatus = "pending",
    interestRate = 10,
    tenureMonths = 12,
  } = userDetails;

  const monthlyInterestRate = interestRate / 12 / 100;
  const emi =
    (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, tenureMonths)) /
    (Math.pow(1 + monthlyInterestRate, tenureMonths) - 1);
  const totalPayment = emi * tenureMonths;
  const interest = totalPayment - loanAmount;

  const pieData = [
    { name: "Principal", value: loanAmount },
    { name: "Interest", value: interest },
    { name: "Paid", value: loanAmount * 0.1 },
  ];

  const getStatusColor = (status) => STATUS_COLORS[status] || "#95a5a6";

  return (
    <>
      <ResponsiveAppBar />
      <Box
        sx={{
          background: "linear-gradient(135deg, #eceff1 0%, #cfd8dc 100%)",
          color: "#2c3e50",
          padding: { xs: 2, md: 3 },
          minHeight: "100vh",
        }}
      >
        <Fade in={true} timeout={600}>
          <Grid container spacing={3} sx={{ maxWidth: 1200, mx: "auto" }}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: "12px",
                  background: "#ffffff",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
              >
                <Typography variant="h4" fontWeight="600" color="#2c3e50" gutterBottom>
                  {name}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {email}
                </Typography>
                <Chip
                  label={loanStatus}
                  sx={{
                    backgroundColor: getStatusColor(loanStatus),
                    color: "white",
                    fontWeight: "medium",
                    mb: 2,
                  }}
                />
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <AccountBalanceIcon sx={{ mr: 1, color: "#3498db" }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Loan Type</Typography>
                      <Typography variant="body1" fontWeight="500">{loanType}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CreditCardIcon sx={{ mr: 1, color: "#3498db" }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Loan Amount</Typography>
                      <Typography variant="body1" fontWeight="500">₹{loanAmount.toLocaleString()}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PaymentsIcon sx={{ mr: 1, color: "#3498db" }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Monthly EMI</Typography>
                      <Typography variant="body1" fontWeight="500">₹{emi.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarMonthIcon sx={{ mr: 1, color: "#3498db" }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Interest Rate</Typography>
                      <Typography variant="body1" fontWeight="500">{interestRate}% p.a.</Typography>
                    </Box>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<LogoutIcon />}
                  onClick={logoutUser}
                  sx={{
                    mt: 3,
                    borderRadius: "8px",
                    textTransform: "none",
                    color: "#e74c3c",
                    borderColor: "#e74c3c",
                    "&:hover": { borderColor: "#c0392b", color: "#c0392b" },
                  }}
                >
                  Logout
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: "12px",
                    background: "#ffffff",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h5" fontWeight="600" color="#2c3e50">
                      Loan Breakdown
                    </Typography>
                    <IconButton
                      onClick={() => setInfoOpen(true)}
                      sx={{ color: "#3498db", "&:hover": { backgroundColor: "rgba(52, 152, 219, 0.1)" } }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                    <Box sx={{ width: { xs: "100%", sm: "50%" }, height: 250 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            innerRadius={50}
                            paddingAngle={2}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {pieData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                                stroke="#fff"
                                strokeWidth={1}
                              />
                            ))}
                          </Pie>
                          <Legend verticalAlign="bottom" />
                          <RechartTooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ width: { xs: "100%", sm: "50%" }, display: "flex", flexDirection: "column", gap: 2 }}>
                      <Paper sx={{ p: 2, backgroundColor: "rgba(46, 204, 113, 0.05)", borderRadius: "8px" }}>
                        <Typography variant="caption" color="text.secondary">Principal Amount</Typography>
                        <Typography variant="h6" color="#2ecc71" fontWeight="600">
                          ₹{loanAmount.toLocaleString()}
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 2, backgroundColor: "rgba(230, 126, 34, 0.05)", borderRadius: "8px" }}>
                        <Typography variant="caption" color="text.secondary">Total Interest</Typography>
                        <Typography variant="h6" color="#e67e22" fontWeight="600">
                          ₹{interest.toFixed(2)}
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 2, backgroundColor: "rgba(52, 152, 219, 0.05)", borderRadius: "8px" }}>
                        <Typography variant="caption" color="text.secondary">Amount Paid</Typography>
                        <Typography variant="h6" color="#3498db" fontWeight="600">
                          ₹{(loanAmount * 0.1).toFixed(2)}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                </Paper>
                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: "12px",
                    background: "#ffffff",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography variant="h5" fontWeight="600" color="#2c3e50" gutterBottom>
                    Payment Schedule
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, backgroundColor: "rgba(52, 152, 219, 0.05)", borderRadius: "8px" }}>
                        <Typography variant="caption" color="text.secondary">Next Payment Due</Typography>
                        <Typography variant="h6" fontWeight="500">May 15, 2025</Typography>
                        <Typography variant="body1" fontWeight="600" color="#3498db">
                          ₹{emi.toFixed(2)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} >
                      <Paper sx={{ p: 2, backgroundColor: "rgba(46, 204, 113, 0.05)", borderRadius: "8px" }}>
                        <Typography variant="caption" color="text.secondary">Total Remaining</Typography>
                        <Typography variant="h6" fontWeight="500">{tenureMonths - 1} installments</Typography>
                        <Typography variant="body1" fontWeight="600" color="#2ecc71">
                          ₹{(totalPayment - loanAmount * 0.1).toFixed(2)}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      borderRadius: "8px",
                      textTransform: "none",
                      backgroundColor: "#3498db",
                      "&:hover": { backgroundColor: "#2980b9" },
                    }}
                  >
                    View Full Schedule
                  </Button>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Fade>
      </Box>
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", maxWidth: 600 } }}
      >
        <DialogTitle sx={{ backgroundColor: "#3498db", color: "white", fontWeight: "500" }}>
          Loan Details
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Principal Amount</Typography>
              <Typography variant="h6">₹{loanAmount.toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Interest Rate
              </Typography>
              <Typography variant="h6">{interestRate}% p.a.</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Loan Tenure
              </Typography>
              <Typography variant="h6">{tenureMonths} months</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Monthly EMI</Typography>
              <Typography variant="h6">₹{emi.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Total Interest
              </Typography>
              <Typography variant="h6">₹{interest.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Total Payable
              </Typography>
              <Typography variant="h6">₹{totalPayment.toFixed(2)}</Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Status: <strong style={{ color: getStatusColor(loanStatus) }}>{loanStatus}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Contact support at support@loanapp.com for assistance.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setInfoOpen(false)}
            sx={{ textTransform: "none", color: "#3498db" }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#3498db",
              "&:hover": { backgroundColor: "#2980b9" },
            }}
          >
            Download Details
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserDetails;